import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import { asId, type ConversationId, type Message, type MessageId } from '@cyphex/types';
import { chatApi } from '../api';
import { markdownToRich } from '../lib/markdown/blocks';
import { optimisticReactions } from '../lib/optimistic';
import { dropMessage, patchMessage, tombstoneMessage } from '../lib/timelineCache';
import { outboxStore } from '../store/outboxStore';
import { useMe } from './useMe';
import { cachedMentionTargets } from './useMentionPicker';

export interface MessageActions {
  toggleReaction: (message: Message, emoji: string) => void;
  edit: (messageId: MessageId, markdown: string) => void;
  remove: (messageId: MessageId) => void;
  togglePin: (message: Message) => void;
  retry: (clientMsgId: string) => void;
  /** Re-arm a send the server refused with a data-protection WARNING. */
  sendAnyway: (clientMsgId: string) => void;
  discard: (clientMsgId: string) => void;
}

/** Optimistic message mutations. Every one rolls back visibly on failure. */
export function useMessageActions(conversationId: ConversationId | null): MessageActions {
  const client = useQueryClient();
  const meId = useMe()?.id ?? null;

  const toggleReaction = useCallback(
    (message: Message, emoji: string) => {
      if (!conversationId) return;
      const reacted = message.reactions.find((reaction) => reaction.emoji === emoji)?.reacted ?? false;
      const previous = message.reactions;

      patchMessage(client, conversationId, message.id, (current) => ({
        ...current,
        reactions: optimisticReactions(current.reactions, emoji, reacted),
      }));

      const call = reacted ? chatApi.removeReaction : chatApi.addReaction;
      void call(conversationId, message.id, emoji)
        .then((reactions) =>
          patchMessage(client, conversationId, message.id, (current) => ({ ...current, reactions })),
        )
        .catch(() => {
          patchMessage(client, conversationId, message.id, (current) => ({
            ...current,
            reactions: previous,
          }));
          toast.error('Could not save that reaction.');
        });
    },
    [client, conversationId],
  );

  const edit = useCallback(
    (messageId: MessageId, markdown: string) => {
      if (!conversationId) return;
      /* THE DOCUMENT, not only the text. The server's own markdown reader does
         not know who "@Maya Patel" is, so an edit sent as text turned every
         mention chip in the message back into plain words. */
      const body = markdownToRich(markdown, cachedMentionTargets(client, conversationId, meId));
      void chatApi
        .editMessage(conversationId, messageId, { markdown, body })
        .then((message) => patchMessage(client, conversationId, messageId, () => message))
        .catch(() => toast.error('Could not save your edit.'));
    },
    [client, conversationId, meId],
  );

  const remove = useCallback(
    (messageId: MessageId) => {
      if (!conversationId) return;
      tombstoneMessage(client, conversationId, messageId, new Date().toISOString());
      void chatApi
        .deleteMessage(conversationId, messageId)
        .catch(() => toast.error('Could not delete that message.'));
    },
    [client, conversationId],
  );

  const togglePin = useCallback(
    (message: Message) => {
      if (!conversationId) return;
      const next = !message.isPinned;
      patchMessage(client, conversationId, message.id, (current) => ({ ...current, isPinned: next }));
      void chatApi
        .pinMessage(conversationId, message.id, next)
        .catch(() => toast.error('Could not pin that message.'));
    },
    [client, conversationId],
  );

  const retry = useCallback((clientMsgId: string) => {
    void outboxStore.update(clientMsgId, { state: 'queued', attempts: 0, nextAttemptAt: 0, lastError: null });
  }, []);

  /**
   * The sender read the warning and chose to continue. The acknowledgement is
   * a property of THIS send, so it rides on the outbox item and is replayed on
   * every subsequent attempt — a retry after a dropped connection must not
   * silently drop the consent and bounce off the same rule again.
   */
  const sendAnyway = useCallback((clientMsgId: string) => {
    void outboxStore.update(clientMsgId, {
      state: 'queued',
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null,
      dlpAcknowledged: true,
      dlpRefusal: null,
    });
  }, []);

  const discard = useCallback(
    (clientMsgId: string) => {
      if (conversationId) dropMessage(client, conversationId, asId<MessageId>(clientMsgId));
      void outboxStore.remove(clientMsgId);
    },
    [client, conversationId],
  );

  return useMemo(
    () => ({ toggleReaction, edit, remove, togglePin, retry, sendAnyway, discard }),
    [discard, edit, remove, retry, sendAnyway, togglePin, toggleReaction],
  );
}
